import { AlertCircle, Building2, RefreshCw, ShieldCheck, ShieldOff, User } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useTranslations } from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { colocaToast } from "@/shared/utils/toast";

import { KycStatusPill } from "../components/KycStatusPill";
import { VerificationService } from "../services/verificationService";
import type { AccountVerificationResponse } from "../types/verification-api.types";

const EMPTY = "—";

/**
 * `/app/account-verification` — INTERNAL staff only.
 *
 * Reads one organization's own KYC/KYB verification (`GET /internal/account/verification?organization_id=`)
 * and lifts its identity-change cooldown (`POST …/identity-change-waiver`). Members reach operations through
 * "Request an early change" in Settings → Verification, which posts a Discord alert; this page is where that
 * request is honoured. The backend guard (`identities:write` + INTERNAL) is the real boundary — the registry
 * entry mirrors it so the page never renders for someone the API would reject.
 */
const AccountVerificationAdminPage = () => {
  const { t, activeLanguage } = useTranslations("verification");
  const catalog = useOrganizationsCatalog();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [data, setData] = useState<AccountVerificationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiving, setWaiving] = useState(false);
  // The organization currently shown; a response for a previous pick is dropped instead of rendered.
  const currentIdRef = useRef<string | null>(null);

  const scopeLabels = useMemo(
    () => ({
      label: t("admin_scope_label"),
      placeholder: t("admin_scope_placeholder"),
      loading: t("admin_scope_loading"),
      empty: t("admin_scope_empty"),
      searching: t("admin_scope_searching"),
      searchError: t("admin_scope_search_error"),
      catalogError: t("admin_scope_catalog_error"),
      catalogTruncated: t("admin_scope_catalog_truncated"),
      globalOption: t("admin_scope_global_option"),
      globalHint: t("admin_scope_global_hint"),
      orgHint: t("admin_scope_org_hint"),
    }),
    [t],
  );

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(false);
    try {
      const response = await VerificationService.getAccountVerification(id);
      if (currentIdRef.current !== id) return;
      setData(response.data);
    } catch {
      if (currentIdRef.current !== id) return;
      setData(null);
      setError(true);
    } finally {
      if (currentIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    currentIdRef.current = organizationId;
    if (!organizationId) {
      setData(null);
      setError(false);
      setLoading(false);
      return;
    }
    void load(organizationId);
  }, [organizationId, load]);

  const handleWaive = useCallback(async () => {
    if (!organizationId) return;
    setWaiveOpen(false);
    setWaiving(true);
    try {
      await VerificationService.waiveIdentityChangeCooldown(organizationId);
      colocaToast.success(t("admin_waive_done"));
      await load(organizationId);
    } catch {
      colocaToast.error(t("error_generic_description"));
    } finally {
      setWaiving(false);
    }
  }, [organizationId, load, t]);

  const dateOptions = { locale: activeLanguage, includeTime: true } as const;
  const verification = data?.verification;
  const isBusiness = verification?.subject_kind === "business";

  return (
    <div className="space-y-6 p-2">
      <ColocaPageHeader title={t("admin_title")} description={t("admin_description")} />

      <OrganizationScopePicker
        id="account-verification-scope"
        className="max-w-md"
        value={organizationId}
        onValueChange={setOrganizationId}
        catalog={catalog}
        labels={scopeLabels}
      />

      {!organizationId ? (
        <ColocaEmpty surface="card" icon={ShieldCheck} title={t("admin_pick_org")} />
      ) : loading ? (
        <ColocaSkeleton className="h-40 w-full" />
      ) : error || !data || !verification ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={t("admin_load_error")}
          actions={
            <ColocaButton
              size="sm"
              variant="default"
              icon={RefreshCw}
              onClick={() => void load(organizationId)}
            >
              {t("admin_retry")}
            </ColocaButton>
          }
        />
      ) : (
        <ColocaSurface variant="panel" className="space-y-5 p-5">
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("admin_type_label")}
              </dt>
              <dd className="mt-1">
                {data.has_corporate_identity ? (
                  <ColocaBadge icon={isBusiness ? Building2 : User}>
                    {isBusiness ? t("choose_business_title") : t("choose_person_title")}
                  </ColocaBadge>
                ) : (
                  <span className="text-sm text-muted-foreground">{EMPTY}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("admin_status_label")}
              </dt>
              <dd className="mt-1">
                <KycStatusPill status={data.kyc_status} />
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t("admin_change_label")}
              </dt>
              <dd className="mt-1 text-sm">
                {verification.can_change_identity
                  ? t("admin_change_open")
                  : t("admin_change_blocked", {
                      date: verification.identity_change_available_at
                        ? formatDateTime(verification.identity_change_available_at, dateOptions)
                        : EMPTY,
                    })}
              </dd>
            </div>
          </dl>

          {!verification.can_change_identity && (
            <div className="flex justify-end">
              <ColocaButton
                variant="destructive"
                icon={ShieldOff}
                loading={waiving}
                onClick={() => setWaiveOpen(true)}
              >
                {t("admin_waive")}
              </ColocaButton>
            </div>
          )}
        </ColocaSurface>
      )}

      <ColocaAlertDialog
        open={waiveOpen}
        onOpenChange={setWaiveOpen}
        title={t("admin_waive_confirm_title")}
        description={t("admin_waive_confirm_description")}
        confirmLabel={t("admin_waive")}
        cancelLabel={t("admin_waive_cancel")}
        confirmVariant="destructive"
        onConfirm={() => void handleWaive()}
      />
    </div>
  );
};

export default AccountVerificationAdminPage;
